/**
 * What a consumable does (ADR-091 decision 3 as amended 2026-10-08, the author's ruling): a consumable's heal is halved
 * only while its bearer is in combat with a player, and is whole against creatures alone and out of combat. In combat
 * with a player means the bearer struck a player, or was struck by one, within the combat window (`in_combat`, the
 * window of any hostile action, ADR-073 decision 13), whether or not it struck back; the map server keeps that window
 * per character and tells the page in the `vitals` state bit `InPvpCombat`. The rule applies only to a use that came
 * from an item, so a healing spell is untouched, and both the map server and the page ask this function, so the
 * halving is written once. A consumable's stamina restore is halved by the same rule (ADR-091's note for P2.8-T57,
 * item 2).
 */

/**
 * The points a consumable's heal or stamina restore gives back: its amount, or half of it, rounded down and never below
 * 1, while the bearer is in combat with a player at the tick it lands.
 *
 * @param amount The record's heal or stamina restore amount, a whole number of at least 1.
 * @param inPvpCombat Whether the bearer struck a player or was struck by one within the combat window.
 * @returns The points to restore, for health before any healing reduction.
 * @throws RangeError when the amount is not a whole number of at least 1.
 * @example
 * consumableHeal(30, true);
 * // 15
 */
export function consumableHeal(amount: number, inPvpCombat: boolean): number {
  if (!Number.isInteger(amount) || amount < 1) {
    throw new RangeError(`a heal amount is a whole number of at least 1, not ${amount}`);
  }
  return inPvpCombat ? Math.max(1, Math.floor(amount / 2)) : amount;
}
